/* cart-mundtape-gift — Web Component für das zeitlich begrenzte
 * "30er Mundtape gratis ab 100 €"-Geschenk (Schritt 2 der Aktion).
 *
 * Bewusst eigenständig vom 7er-Selector (cart-gift-selector.js) — der 7er
 * wird NICHT refactored. Gleiche bewährte Patterns, eigene Datei, eigene
 * sessionStorage-Keys, eigene pubsub-Source.
 *
 * Verantwortungen:
 *   1. Auto-Add: wenn Liquid `data-should-add` setzt (Aktion läuft, Schwelle
 *      erreicht, verfügbar, noch keine Line) → einmal add.js mit fixer Variante.
 *   2. Auto-Cleanup: wenn Liquid `data-needs-cleanup` setzt → Line entfernen.
 *      Grund via `data-cleanup-reason` ('threshold' | 'promo-ended') steuert
 *      den Toast-Text.
 *   3. Quantity-Fix: wenn Liquid `data-needs-quantity-fix` setzt → stiller
 *      Reset auf 1 (Sicherheitsnetz, Stepper ist im Cart versteckt).
 *   4. Toast nach erfolgreichem Cleanup (über sessionStorage gepuffert).
 *
 * Loop-Schutz (3-Schicht, wie beim 7er):
 *   - Liquid-Konvergenz: nach jeder Mutation sieht der nächste Render kein
 *     Signal mehr (Line weg / Menge 1 / Line da).
 *   - In-Flight-Gate (sessionStorage): eine laufende Mutation blockt parallele
 *     Instanzen. EIN Gate für alle Cart-Mutationen dieses Components (Mutex).
 *   - Stale-TTL (5s): alte Flags verfallen, falls eine Mutation hängenbleibt.
 *   Zusätzlich beim Auto-Add: Pre-Add-Verify gegen /cart.js — existiert bereits
 *   eine Mundtape-Line, wird NICHT erneut hinzugefügt (Konvergenz-Sicherung
 *   gegen Doppel-Add, falls `data-should-add` mal nachhängt).
 *
 * Integration: publish theme.pubsub cartUpdate mit source='mundtape-gift',
 * KEIN Listener auf cart:refresh / cart:updated (gehören CartDrawer / CartItems).
 *
 * Bewusst NICHT abgesichert (Konsistenz mit 7er, siehe cart-gift-selector.js):
 *   - Multi-Tab-Race: zwei Tabs könnten parallel je ein add.js senden → zwei
 *     Mundtape-Lines. Liquid warnt im HTML-Kommentar (gift_count > 1), Component
 *     fasst nur die erste an. Akzeptiert: sehr seltener Pfad.
 *   - Cross-Gift-Gate: 7er und 30er teilen sich KEIN Gate. Beide sind in sich
 *     sauber abgesichert + konvergieren via Liquid; das kurze Fenster, in dem
 *     beide denselben Cart mutieren, ist akzeptiert.
 */

const MG_PROPERTY_KEY = '_gift';
const MG_PROPERTY_VALUE = 'free-30er-mundtape';
const MG_VISIBLE_PROPERTY = 'Aktionsgeschenk';
const MG_VISIBLE_LABEL = '🎁 Gratis 30er Mundtape ab 100 €';

const MG_SS_IN_FLIGHT = 'sniffys-mundtape-in-flight';
const MG_SS_AUTO_REMOVED = 'sniffys-mundtape-auto-removed';
const MG_STALE_MS = 5000;
const MG_TOAST_VISIBLE_MS = 5000;

const MG_TOAST_MESSAGES = {
  threshold: 'Dein gratis 30er Mundtape wurde entfernt, weil der Mindestbestellwert unterschritten wurde.',
  'promo-ended': 'Die Aktion ist beendet — dein gratis 30er Mundtape wurde aus dem Warenkorb entfernt.',
};

if (!customElements.get('mundtape-gift')) {
  customElements.define('mundtape-gift', class MundtapeGift extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.toast = this.querySelector('[data-toast]');

      this.variantId = this.dataset.variantId || '';
      this.currentGiftKey = this.dataset.currentGiftKey || '';
      this.shouldAdd = this.dataset.shouldAdd === 'true';
      this.needsCleanupKey = this.dataset.needsCleanup || '';
      this.cleanupReason = this.dataset.cleanupReason || '';
      this.needsQuantityFixKey = this.dataset.needsQuantityFix || '';

      this.showPendingToastIfAny();
      // Reihenfolge: erst Cleanup (Line raus), dann Add (Line rein), dann
      // Quantity-Fix. Pro Render ist durch die Liquid-Logik ohnehin höchstens
      // eine dieser Aktionen signalisiert.
      this.maybeRunCleanup();
      this.maybeRunAutoAdd();
      this.maybeRunQuantityFix();
    }

    /* ─── In-Flight-Gate (Mutex auf dem Cart) ────────────────────── */

    acquireGate() {
      let inFlightTs = null;
      try { inFlightTs = sessionStorage.getItem(MG_SS_IN_FLIGHT); } catch (e) {}
      if (inFlightTs) {
        const age = Date.now() - Number(inFlightTs);
        if (!Number.isNaN(age) && age < MG_STALE_MS) {
          return false;
        }
      }
      try { sessionStorage.setItem(MG_SS_IN_FLIGHT, String(Date.now())); } catch (e) {}
      return true;
    }

    releaseGate() {
      try { sessionStorage.removeItem(MG_SS_IN_FLIGHT); } catch (e) {}
    }

    /* ─── Auto-Add ───────────────────────────────────────────────── */

    async maybeRunAutoAdd() {
      if (!this.shouldAdd || !this.variantId) return;
      if (this.isSubmitting) return;
      if (!this.acquireGate()) return;

      this.isSubmitting = true;
      try {
        // Pre-Add-Verify: existiert bereits eine Mundtape-Line, NICHT erneut
        // hinzufügen (Doppel-Add-Schutz, falls data-should-add nachhängt).
        const existing = await this.findGiftLineKey();
        if (existing) return;

        await this.addGiftLine(this.variantId);
        await this.triggerCartRefresh();
        // Liquid-Konvergenz: nächster Render sieht die Line → kein should-add.
      } catch (err) {
        console.warn('[mundtape-gift] auto-add failed', err);
      } finally {
        this.isSubmitting = false;
        this.releaseGate();
      }
    }

    /* ─── Auto-Cleanup ───────────────────────────────────────────── */

    async maybeRunCleanup() {
      if (!this.needsCleanupKey) return;
      if (this.isSubmitting) return;
      if (!this.acquireGate()) return;

      this.isSubmitting = true;
      try {
        const verified = await this.verifyGiftLine(this.needsCleanupKey);
        if (!verified) return;

        await this.removeLine(this.needsCleanupKey);
        // Grund für den Toast puffern (threshold vs. promo-ended).
        const reason = MG_TOAST_MESSAGES[this.cleanupReason] ? this.cleanupReason : 'threshold';
        try { sessionStorage.setItem(MG_SS_AUTO_REMOVED, reason); } catch (e) {}
        await this.triggerCartRefresh();
        // Liquid-Konvergenz: nächster Render sieht keine Line → kein Signal.
      } catch (err) {
        console.warn('[mundtape-gift] auto-cleanup failed', err);
      } finally {
        this.isSubmitting = false;
        this.releaseGate();
      }
    }

    /* ─── Quantity-Fix ───────────────────────────────────────────── */

    async maybeRunQuantityFix() {
      if (!this.needsQuantityFixKey) return;
      if (this.isSubmitting) return;
      if (!this.acquireGate()) return;

      this.isSubmitting = true;
      try {
        const verified = await this.verifyGiftLine(this.needsQuantityFixKey);
        if (!verified) return;

        await this.setLineQuantity(this.needsQuantityFixKey, 1);
        await this.triggerCartRefresh();
      } catch (err) {
        console.warn('[mundtape-gift] quantity-fix failed', err);
      } finally {
        this.isSubmitting = false;
        this.releaseGate();
      }
    }

    /* ─── Toast ──────────────────────────────────────────────────── */

    showPendingToastIfAny() {
      let reason = null;
      try { reason = sessionStorage.getItem(MG_SS_AUTO_REMOVED); } catch (e) {}
      if (!reason) return;
      try { sessionStorage.removeItem(MG_SS_AUTO_REMOVED); } catch (e) {}

      const msg = MG_TOAST_MESSAGES[reason] || MG_TOAST_MESSAGES.threshold;
      this.showToast(msg);
    }

    showToast(message) {
      if (!this.toast) return;
      this.toast.textContent = message;
      this.toast.removeAttribute('hidden');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.setAttribute('hidden', '');
        this.toast.textContent = '';
      }, MG_TOAST_VISIBLE_MS);
    }

    /* ─── Cart-API ──────────────────────────────────────────────── */

    // Liefert den key der ersten Mundtape-Line im Cart, oder '' wenn keine.
    async findGiftLineKey() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        if (!cart || !Array.isArray(cart.items)) return '';
        const item = cart.items.find((i) => {
          const props = i.properties || {};
          return props[MG_PROPERTY_KEY] === MG_PROPERTY_VALUE;
        });
        return item ? item.key : '';
      } catch (err) {
        console.warn('[mundtape-gift] findGiftLineKey failed', err);
        // Im Zweifel KEIN Add auslösen → '' würde Add erlauben, daher
        // signalisieren wir "existiert" durch einen Sentinel.
        return '__error__';
      }
    }

    async verifyGiftLine(lineKey) {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        if (!cart || !Array.isArray(cart.items)) return false;
        const item = cart.items.find((i) => i.key === lineKey);
        if (!item) return false;
        const props = item.properties || {};
        return props[MG_PROPERTY_KEY] === MG_PROPERTY_VALUE;
      } catch (err) {
        console.warn('[mundtape-gift] verifyGiftLine failed', err);
        return false;
      }
    }

    async removeLine(lineKey) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (!res.ok) {
        throw new Error('change.js failed: ' + res.status);
      }
      return res.json();
    }

    async setLineQuantity(lineKey, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      });
      if (!res.ok) {
        throw new Error('change.js failed: ' + res.status);
      }
      return res.json();
    }

    collectSectionsToBundle() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections },
      }));
      return sections;
    }

    async addGiftLine(variantId) {
      const sections = this.collectSectionsToBundle();
      const properties = { [MG_PROPERTY_KEY]: MG_PROPERTY_VALUE };
      properties[MG_VISIBLE_PROPERTY] = MG_VISIBLE_LABEL;

      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties }],
        sections,
        sections_url: window.location.pathname,
      });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        credentials: 'same-origin',
        body,
      });
      const parsed = await res.json();
      if (parsed && parsed.status) {
        throw new Error(parsed.description || parsed.message || 'add.js error');
      }
      this._lastAddResponse = parsed;
      return parsed;
    }

    async triggerCartRefresh() {
      let cartJson = null;
      try {
        const cartUrl = (window.theme && theme.routes && theme.routes.cart_url) || '/cart.js';
        const fetchConfig = (window.theme && theme.utils && theme.utils.fetchConfig)
          ? theme.utils.fetchConfig('json', 'GET')
          : { method: 'GET', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
        cartJson = await (await fetch(cartUrl, fetchConfig)).json();
        if (cartJson && this._lastAddResponse && this._lastAddResponse.sections) {
          cartJson.sections = this._lastAddResponse.sections;
        }
      } catch (err) {
        console.warn('[mundtape-gift] cart fetch failed', err);
      }

      try {
        if (window.theme && theme.pubsub && cartJson) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'mundtape-gift',
            cart: cartJson,
          });
        }
      } catch (err) {
        console.warn('[mundtape-gift] pubsub publish failed', err);
      }

      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {}
    }
  });
}
